import { Controller } from "@hotwired/stimulus"

// Email Template Picker (Trello #153)
//
// A modal launched from the inbox "New email" split button. Two steps:
//   1. Choose a template (browse / search).
//   2. Choose a tenancy (resolves the auto variables) + fill any manual fields.
// On Build, renders server-side and hands the finished email to the inbox
// composer via the existing `open-email-compose` window event.
export default class extends Controller {
  static targets = [
    "overlay", "step1", "step2", "list", "search",
    "chosenName", "tenancyInput", "tenancyResults", "variables", "buildButton"
  ]

  connect() {
    this._onOpen = (e) => this.open(e?.detail)
    window.addEventListener("open-template-picker", this._onOpen)
    // Pre-load the list so opening is instant — no content pop-in flicker.
    this._listReady = this.loadList()
  }

  disconnect() {
    window.removeEventListener("open-template-picker", this._onOpen)
  }

  loadList() {
    return this.fetchHtml(this.data.get("pickerUrl"))
      .then(html => { this.listTarget.innerHTML = html })
      .catch(() => {})
  }

  async open(detail) {
    this.reset()
    // Only reveal once the list is drawn (and the correct step is set), so the
    // modal never appears half-built.
    await this._listReady
    const preselectId = detail && detail.templateId
    if (preselectId) this.preselect(preselectId)

    this.overlayTarget.classList.remove("hidden")
    if (preselectId) this.tenancyInputTarget?.focus()
    else this.searchTarget?.focus()
  }

  // Jump straight to the tenancy step for a known template (deep-link entry).
  preselect(id) {
    const btn = this.listTarget.querySelector(`[data-id="${id}"]`)
    if (btn) this.choose({ currentTarget: btn })
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    this.reset()
  }

  // Close when clicking the backdrop (but not the panel). Only close when the
  // press STARTED on the backdrop — otherwise selecting text in an input and
  // releasing the mouse over the backdrop would close the modal.
  backdropDown(e) {
    this._downOnBackdrop = e.target === this.overlayTarget
  }

  backdrop(e) {
    if (e.target === this.overlayTarget && this._downOnBackdrop) this.close()
    this._downOnBackdrop = false
  }

  reset() {
    this.templateId = null
    this.tenancyId = null
    this.subjectKind = null
    this.preTenancy = false
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
    if (this.hasSearchTarget) this.searchTarget.value = ""
    if (this.hasTenancyInputTarget) this.tenancyInputTarget.value = ""
    if (this.hasTenancyResultsTarget) this.tenancyResultsTarget.innerHTML = ""
    if (this.hasVariablesTarget) this.variablesTarget.innerHTML = ""
    this.setBuildEnabled(false)
  }

  // Step 1 — client-side filter of the loaded list.
  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this.listTarget.querySelectorAll("[data-search]").forEach(el => {
      const match = !q || el.dataset.search.includes(q)
      el.classList.toggle("hidden", !match)
    })
    this.listTarget.querySelectorAll("[data-group]").forEach(group => {
      const anyVisible = group.querySelector("[data-search]:not(.hidden)")
      group.classList.toggle("hidden", !anyVisible)
    })
  }

  choose(e) {
    const btn = e.currentTarget
    this.templateId = btn.dataset.id
    // Pre-tenancy templates also search in-flight applications — the let
    // exists before its tenancy record does.
    this.preTenancy = btn.dataset.preTenancy === "true"
    this.chosenNameTarget.textContent = btn.dataset.name
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.remove("hidden")
    this.tenancyInputTarget.focus()
  }

  back() {
    this.tenancyId = null
    this.variablesTarget.innerHTML = ""
    this.tenancyResultsTarget.innerHTML = ""
    this.setBuildEnabled(false)
    this.step2Target.classList.add("hidden")
    this.step1Target.classList.remove("hidden")
    this.searchTarget?.focus()
  }

  // Step 2 — debounced tenancy typeahead. The server also returns in-flight
  // applications when the chosen template is authored for the pre-tenancy
  // stage (template_id tells it which gate applies).
  searchTenancy() {
    clearTimeout(this._tenancyTimer)
    const q = this.tenancyInputTarget.value.trim()
    if (q.length < 2) { this.tenancyResultsTarget.innerHTML = ""; return }
    this._tenancyTimer = setTimeout(() => {
      const url = `${this.data.get("tenancySearchUrl")}?q=${encodeURIComponent(q)}&template_id=${this.templateId}`
      fetch(url, { headers: { Accept: "application/json" } })
        .then(r => r.json())
        .then(rows => this.renderTenancyResults(rows))
    }, 250)
  }

  renderTenancyResults(rows) {
    if (!rows.length) {
      const scope = this.preTenancy ? "tenancies or applications" : "tenancies"
      this.tenancyResultsTarget.innerHTML =
        `<div class="px-3 py-2 text-[13px] text-gray-400">No matching ${scope}</div>`
      return
    }
    this.tenancyResultsTarget.innerHTML = rows.map(r => `
      <button type="button" data-action="click->email-template-picker#chooseTenancy"
        data-id="${r.id}" data-kind="${r.kind || "tenancy"}" data-label="${this.escape(r.label)}"
        class="block w-full text-left px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 rounded-lg">
        ${this.escape(r.label)}${r.kind === "application" ? ' <span class="text-gray-400">· application</span>' : ""}
      </button>`).join("")
  }

  chooseTenancy(e) {
    const btn = e.currentTarget
    this.tenancyId = btn.dataset.id
    this.subjectKind = btn.dataset.kind || "tenancy"
    this.tenancyInputTarget.value = btn.dataset.label
    this.tenancyResultsTarget.innerHTML = ""
    const url = `${this.data.get("variablesUrlTemplate")}`
      .replace("__ID__", this.templateId) + `?${this.subjectParam}=${this.tenancyId}`
    this.fetchHtml(url).then(html => {
      this.variablesTarget.innerHTML = html
      this.setBuildEnabled(true)
    })
  }

  get subjectParam() {
    return this.subjectKind === "application" ? "application_id" : "tenancy_id"
  }

  build() {
    if (!this.templateId || !this.tenancyId) return
    const url = `${this.data.get("buildUrlTemplate")}`.replace("__ID__", this.templateId)
    const body = new FormData()
    body.append(this.subjectParam, this.tenancyId)
    this.variablesTarget
      .querySelectorAll("[name^='variables[']")
      .forEach(input => body.append(input.name, input.value))

    this.setBuildEnabled(false)
    fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      body
    })
      .then(r => {
        if (!r.ok) throw new Error(`Build failed (${r.status})`)
        return r.json()
      })
      .then(detail => {
        window.dispatchEvent(new CustomEvent("open-email-compose", { detail }))
        this.close()
      })
      .catch(err => {
        console.error("Email template build failed:", err)
        this.setBuildEnabled(true)
      })
  }

  setBuildEnabled(enabled) {
    if (!this.hasBuildButtonTarget) return
    this.buildButtonTarget.disabled = !enabled
    this.buildButtonTarget.classList.toggle("opacity-50", !enabled)
    this.buildButtonTarget.classList.toggle("cursor-not-allowed", !enabled)
  }

  fetchHtml(url) {
    return fetch(url, { headers: { Accept: "text/html" } }).then(r => r.text())
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }
}
